import { Matrix4, Vector3 } from 'three';
import { axesNode, type AxesNode } from '@/lib/threeScene/nodes/axes';
import { gridNode, type GridNode } from '@/lib/threeScene/nodes/grid';
import LPS from '@/domain/anatomy/LPS';
import { type CameraState } from '@/lib/threeScene/camera';
import { createImageViewerCameraState } from '@/lib/threeScene/cameras/imageViewerCamera';
import { overlayMeshNode, type OverlayMeshNode } from '@/views/dicoms/overlay/scene/overlayMesh';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import { type OverlayImageNode, overlayImageNode } from '@/views/dicoms/overlay/scene/overlayImageNode';
import { multiplyScalar } from '@/domain/geometry/vector3';
import type { SideId } from "@/domain/anatomy/side";


const CAMERA_DISTANCE = 450 as const;
const OVERLAY_FRUSTUM_SIZE = 2000;
const OVERLAY_CAMERA_NEAR = -5000;
const OVERLAY_CAMERA_FAR = 5000;
const OVERLAY_CAMERA_ZOOM = 10;
const DEFAULT_UP = LPS.Superior;
const DEFAULT_OFFSET_DIRECTION = LPS.Anterior;

/** Nodes that represent the 3D scene for a single view */
export type CatstackOverlayNodes = {
    camera: CameraState;

    ctOrigin: AxesNode;

    sceneCentre: AxesNode;
    sceneGrid: GridNode;

    operativeFemur: OverlayMeshNode;

    catStack: OverlayImageNode;
};

/**
 * Calculate initial camera placement for a given view mode
 */
function _getInitialCameraPlacement(planeId: CatstackPlaneId, sceneCentre: Vector3, operativeSide?: SideId) {
    const target = sceneCentre.clone();
    let offsetDirection: Vector3;
    let up: Vector3;

    switch (planeId) {
        case 'axial':
            offsetDirection = LPS.Inferior;
            up = LPS.Anterior;
            break;
        case 'coronal':
            offsetDirection = LPS.Anterior;
            up = LPS.Superior;
            break;
        case 'sagittal':
            offsetDirection = operativeSide ? LPS.lateral(operativeSide) : LPS.Left;
            up = LPS.Superior;
            break;
        default:
            offsetDirection = DEFAULT_OFFSET_DIRECTION;
            up = DEFAULT_UP;
            break;
    }

    const position = target.clone().add(
        multiplyScalar(offsetDirection, CAMERA_DISTANCE)
    );

    return {
        target,
        position,
        up: up.clone(),
    };
}

export function centerCameraNodeOnSceneCentre(
    camera: CameraState,
    sceneCentre: Vector3,
    planeId: CatstackPlaneId,
    operativeSide: SideId
) {
    const placement = _getInitialCameraPlacement(planeId, sceneCentre, operativeSide);

    camera.target.copy(placement.target);
    camera.position.copy(placement.position);
    camera.up.copy(placement.up);
}

export function catstackOverlayNodes(planeId: CatstackPlaneId): CatstackOverlayNodes {

    return {
        camera: createImageViewerCameraState({
            height: OVERLAY_FRUSTUM_SIZE,
            zoom: OVERLAY_CAMERA_ZOOM,
            near: OVERLAY_CAMERA_NEAR,
            far: OVERLAY_CAMERA_FAR,
        }),

        ctOrigin: axesNode('ct-origin'),
        sceneCentre: axesNode('scene-centre'),
        sceneGrid: gridNode('scene-grid', {
            transform: new Matrix4().makeRotationAxis(LPS.Left, Math.PI / 2),
        }),

        operativeFemur: overlayMeshNode(`operative-femur`),

        catStack: overlayImageNode(planeId),
    };
}
